import dayjs, { Dayjs } from 'dayjs';

/**
 * The one place that says what a rental period MEANS, for the mode that
 * prices by whole days ('Overnight' -- the legacy value name, kept because
 * it's persisted on every existing Machinery/Booking row; everything the
 * guest ever reads calls it "por días").
 *
 * A machine is not a hotel room. Renting the 17th and the 18th is TWO days
 * of machine, not "one night": the 18th isn't a check-out morning the next
 * customer can already have, it's a full day this customer has the machine
 * on their site and nobody else can. The whole checkout was inherited from
 * a nightly-stay platform, where counting the boundaries CROSSED (nights)
 * is the right unit; here the right unit is the days OCCUPIED.
 *
 * So `startAt`/`endAt` stay a half-open interval `[startAt, endAt)` -- which
 * is what the backend's availability, overlap and pricing math has always
 * assumed, and why nothing there had to change -- but the last day the guest
 * picks is now INSIDE it: picking the 17th and the 18th produces
 * `[17th 00:00, 19th 00:00)`, two days long, priced x2, and blocking both
 * the 17th and the 18th for everyone else. Under the old reading the same
 * two clicks produced `[17th, 18th)`, billed one day and left the 18th
 * bookable by somebody else while this guest still had the machine.
 *
 * The catch is that `endAt` is then a day nobody rents, so it must never be
 * shown to anyone: every screen that prints a rental's last day goes through
 * lastRentalDay() below, never through `endAt` raw.
 */

/**
 * True for the mode priced by whole days. 'Overnight' is the stored value;
 * nothing user-facing says "night". Takes a plain string because IBooking
 * carries rentalMode untyped (unlike the widget's own BookingRentalMode).
 */
export const isDailyRentalMode = (mode: string): boolean => mode === 'Overnight';

/**
 * The last day the guest actually has the machine, given a half-open
 * `endAt`. Only meaningful for the daily mode -- an Hourly/DayPass booking's
 * `endAt` is a real instant on a real day and must be shown as-is.
 */
export const lastRentalDay = (endAt: string | Dayjs): Dayjs => dayjs(endAt).subtract(1, 'day');

/**
 * Days billed for a daily-mode range, counted inclusively from the two days
 * the guest actually picked -- `firstDay` and `lastDay` are both days they
 * have the machine, so the 17th-to-the-18th is 2, not 1.
 */
export const countRentalDaysInclusive = (firstDay: Dayjs, lastDay: Dayjs): number =>
  lastDay.startOf('day').diff(firstDay.startOf('day'), 'day') + 1;

/**
 * The half-open `endAt` a daily-mode selection ending on `lastDay` turns
 * into -- midnight opening the day AFTER the last rented one.
 */
export const exclusiveEndOfDailyRange = (lastDay: Dayjs): Dayjs => lastDay.startOf('day').add(1, 'day');
